import { Inject, Injectable } from '@angular/core';

import { catchError, map, Observable, of, tap } from 'rxjs';

import { ApiClient } from '../api-client.service';
import { ClaimTypes } from './claim-types';

export interface Claim {
  type: string;
  value: string;
}

export type User = {
  isAuthenticated: boolean;
  claims?: Claim[];
  /**
   * Flat list of permission names the user has been granted (through their roles),
   * as returned by the `auth/getuser` endpoint. Example: `['Entity.View', 'Asset.Update']`.
   */
  permissions?: string[];
};

export const AUTH_CONFIG = 'auth_config';

export type AuthConfig = {
  authUrl: string;
  loginUrl: string;
  logoutUrl: string;
};

@Injectable()
export class BffUserService {
  constructor(
    private client: ApiClient,
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig
  ) {}

  private _storedUser: User | undefined;

  public get user(): Observable<User> {
    if (this._storedUser) {
      return of(this._storedUser);
    }

    return this.client.get<User>(this.authConfig.authUrl).pipe(
      tap((user: User) => (this._storedUser = user)),
      map((user: User) => user)
    );
  }

  public get roles$(): Observable<string[]> {
    return this.user.pipe(
      map(
        (x) =>
          x.claims
            ?.filter((c) => c.type === ClaimTypes.Role)
            .map((c) => c.value)
            .filter((x) => !!x) || []
      )
    );
  }

  /**
   * The user's permission names. Empty array when unauthenticated or when the
   * backend returned no permissions. Emits once the user has been loaded.
   */
  public get permissions$(): Observable<string[]> {
    return this.user.pipe(
      map((user) => (user.isAuthenticated ? user.permissions ?? [] : []))
    );
  }

  hasPermission(permission: string): Observable<boolean> {
    return this.hasAllPermissions([permission]);
  }

  hasAllPermissions(permissions: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map((granted) => permissions.every((p) => granted.includes(p)))
    );
  }

  hasAnyPermission(permissions: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map((granted) => permissions.some((p) => granted.includes(p)))
    );
  }

  isAuthenticated(): Observable<boolean> {
    return this.user.pipe(map((user) => user.isAuthenticated));
  }

  isNotAuthenticated(): Observable<boolean> {
    return this.isAuthenticated().pipe(
      map((isAuthenticated) => !isAuthenticated),
      catchError((error) => {
        console.error('Error checking authentication', error);
        return of(true);
      })
    );
  }

  login(baseHref?: string) {
    console.log('In BffUserService: login');

    let loginUrl = '/api/Auth/Login';

    if (baseHref) {
      loginUrl += `?returnUrl=${baseHref}`;
    }

    window.location.href = loginUrl;
  }

  logout(baseHref?: string) {
    localStorage.clear();

    let logoutUrl = '/api/Auth/Logout';

    if (baseHref) {
      logoutUrl += `?returnUrl=${baseHref}`;
    }

    window.location.href = logoutUrl;
  }

  matchesClaim(claim: string): Observable<boolean> {
    return this.matchesClaims([claim]);
  }

  matchesClaims(claims: string[]): Observable<boolean> {
    return this.user.pipe(
      map((user) => {
        if (!user.isAuthenticated) return false;

        for (const claim of claims) {
          const hasClaim = this.userHasClaim(user, claim);
          if (!hasClaim) return false;
        }

        return true;
      })
    );
  }

  matchesAnyClaim(claims: string[]): Observable<boolean> {
    return this.user.pipe(
      map((user) => {
        if (!user.isAuthenticated) return false;

        for (const claim of claims) {
          if (this.userHasClaim(user, claim)) return true;
        }

        return false;
      })
    );
  }

  matchesExactClaim(claim: Claim): Observable<boolean> {
    return this.matchesExactClaims([claim]);
  }

  matchesExactClaims(claims: Claim[]): Observable<boolean> {
    return this.user.pipe(
      map((user) => {
        if (!user.isAuthenticated) return false;

        for (const claim of claims) {
          const hasClaim = this.userHasExactClaim(user, claim);
          if (!hasClaim) return false;
        }

        return true;
      })
    );
  }

  getClaimValue(type: string): Observable<string | undefined> {
    return this.user.pipe(
      map((user) => {
        if (!user.isAuthenticated || !user.claims) return undefined;

        return user.claims.find((c) => c.type === type)?.value;
      })
    );
  }

  private userHasExactClaim(user: User, claim: Claim): boolean {
    if (!user.claims) return false;
    return (
      user.claims.findIndex(
        (c) => c.type === claim.type && c.value === claim.value
      ) > -1
    );
  }

  private userHasClaim(user: User, claim: string): boolean {
    if (!user.claims) return false;
    return user.claims.findIndex((c) => c.value === claim) > -1;
  }

  public get userName(): Observable<string | undefined> {
    return this.user.pipe(
      map((user) => {
        if (!user.isAuthenticated) return undefined;
        return user.claims?.find(
          (c) =>
            c.type ===
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
        )?.value;
      })
    );
  }
}
